<template>
  <lb-dialog
    :value="value"
    :heading="title"
    :width="dialogWidth"
    :loading="loading"
    content-class="upsi-review-dialog"
    @input="onInput"
  >
    <template #body>
      <div ref="formBody" class="upsi-review-dialog__body" data-testid="upsi-review-dialog">
        <p class="upsi-review-dialog__lead">{{ bannerMessage }}</p>
        <UpsiSummaryList :rows="summaryRows" />
        <UpsiActivityHistory
          :history="historyRows"
          :filing-reference="filingReference"
          test-id-prefix="upsi-review-dialog"
        />

        <div class="upsi-review-dialog__field">
          <label
            class="upsi-review-dialog__label"
            for="upsi-review-dialog__remarks-input"
          >
            {{ remarksLabel }}
          </label>
          <textarea
            id="upsi-review-dialog__remarks-input"
            class="upsi-review-dialog__input"
            :class="{ 'upsi-review-dialog__input--error': !!remarksError }"
            :value="remarks"
            :placeholder="remarksPlaceholder"
            :maxlength="remarksMax"
            :disabled="loading"
            data-testid="upsi-review-dialog__remarks-input"
            @input="onRemarksInput"
          />
          <p
            v-if="remarksError"
            class="upsi-review-dialog__error"
            data-testid="upsi-review-dialog__remarks-error"
          >
            {{ remarksError }}
          </p>
        </div>
      </div>
    </template>
    <template #actions>
      <div class="upsi-review-dialog__footer">
        <button
          type="button"
          class="upsi-review-dialog__reject"
          :disabled="loading"
          data-testid="upsi-review-dialog__reject-btn"
          @click="onReject"
        >
          {{ rejectLabel }}
        </button>
        <button
          type="button"
          class="upsi-review-dialog__approve"
          :disabled="loading"
          data-testid="upsi-review-dialog__approve-btn"
          @click="onApprove"
        >
          {{ approveLabel }}
        </button>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './UpsiReviewDialog.scss';
import { UpsiActivityHistory, UpsiSummaryList } from '../upsi';
import { UPSI_DECISIONS, UPSI_LIMITS, UPSI_MESSAGES } from '../../../../utils/constants';
import {
  UPSI_DIALOG_WIDTH,
  UPSI_EMPTY,
  UPSI_FIELD_LABELS,
  UPSI_NAME_FALLBACK,
  UPSI_SUMMARY_LABELS,
  displayName,
  fillUpsiTemplate,
  formatPublicationDateTime,
  readServerError,
  scrollUpsiDialogToBottom,
  upsiStageLabel,
  upsiStateOf,
} from '../../components/upsi/upsiDialogUtils';

/** Approve or reject a publish request. Does not send request id or version. */
export default {
  name: 'UpsiReviewDialog',

  components: {
    UpsiActivityHistory,
    UpsiSummaryList,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    cycle: {
      type: Object,
      default: () => ({}),
    },
    details: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      remarks: '',
      remarksClientError: '',
      remarksTouched: false,
      dialogWidth: UPSI_DIALOG_WIDTH,
      title: UPSI_MESSAGES.DIALOG_TITLE,
      remarksLabel: UPSI_FIELD_LABELS.REMARKS,
      remarksPlaceholder: UPSI_FIELD_LABELS.REMARKS_PLACEHOLDER,
      rejectLabel: UPSI_FIELD_LABELS.REJECT,
      approveLabel: UPSI_FIELD_LABELS.APPROVE,
      remarksMax: UPSI_LIMITS.REMARKS_MAX,
    };
  },

  computed: {
    upsiState() {
      return upsiStateOf(this.details);
    },
    requesterName() {
      return displayName(this.upsiState.requested_by_name, UPSI_NAME_FALLBACK.REQUESTER);
    },
    bannerMessage() {
      return UPSI_MESSAGES.BANNER_REVIEW;
    },
    cycleName() {
      const fromDetails = this.details && this.details.cycle && this.details.cycle.description;
      const fromCycle = this.cycle && this.cycle.description;
      return fromDetails || fromCycle || UPSI_EMPTY;
    },
    filingReference() {
      return this.upsiState.filing_reference || '';
    },
    summaryRows() {
      const stage = this.upsiState.stage == null ? 0 : this.upsiState.stage;
      return [
        { label: UPSI_SUMMARY_LABELS.CYCLE, value: this.cycleName },
        { label: UPSI_SUMMARY_LABELS.UPSI_STATUS, value: upsiStageLabel(stage) },
        { label: UPSI_SUMMARY_LABELS.FILING_REFERENCE, value: this.filingReference || UPSI_EMPTY },
        {
          label: UPSI_SUMMARY_LABELS.PUBLICATION_DATE_TIME,
          value: formatPublicationDateTime(this.upsiState.publication_at),
        },
      ];
    },
    historyRows() {
      return this.details && Array.isArray(this.details.history) ? this.details.history : [];
    },
    remarksError() {
      if (this.remarksClientError) return this.remarksClientError;
      if (this.remarksTouched) return '';
      return readServerError(this.serverErrors, ['remarks', 'review_remarks']);
    },
    cycleId() {
      return this.cycle && this.cycle._id;
    },
  },

  watch: {
    value(open) {
      if (open) this.seedForm();
    },
    remarksError(message) {
      if (message) this.scrollValidationIntoView();
    },
    cycleId(next, prev) {
      if (this.value && next && next !== prev) this.seedForm();
    },
  },

  created() {
    if (this.value) this.seedForm();
  },

  beforeDestroy() {
    this.remarks = '';
    this.remarksClientError = '';
    this.remarksTouched = false;
  },

  methods: {
    seedForm() {
      console.log('[UpsiReviewDialog]:', { open: true });
      this.remarks = '';
      this.remarksClientError = '';
      this.remarksTouched = false;
    },
    onRemarksInput(event) {
      const value = event.target.value || '';
      this.remarks = value.slice(0, UPSI_LIMITS.REMARKS_MAX);
      this.remarksClientError = '';
      this.remarksTouched = true;
    },
    onReject() {
      const remarks = this.remarks.trim();
      this.remarksTouched = false;
      if (!remarks) {
        this.remarksClientError = fillUpsiTemplate(UPSI_MESSAGES.REJECT_REMARKS_REQUIRED, {
          requester: this.requesterName,
        });
        this.scrollValidationIntoView();
        return;
      }
      console.log('[UpsiReviewDialog]:', { decision: UPSI_DECISIONS.REJECT });
      this.remarksClientError = '';
      this.$emit('submit', { decision: UPSI_DECISIONS.REJECT, remarks });
    },
    onApprove() {
      const remarks = this.remarks.trim();
      this.remarksTouched = false;
      this.remarksClientError = '';
      console.log('[UpsiReviewDialog]:', { decision: UPSI_DECISIONS.APPROVE });
      this.$emit('submit', { decision: UPSI_DECISIONS.APPROVE, remarks });
    },
    onInput(open) {
      this.$emit('input', open);
      if (!open) this.$emit('cancel');
    },
    scrollValidationIntoView() {
      this.$nextTick(() => {
        scrollUpsiDialogToBottom(this.$refs.formBody);
      });
    },
  },
};
</script>
